import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { Box, Button, Flex, HStack, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea, VStack, useDisclosure } from '@chakra-ui/react'
import { MdDeleteOutline, MdOutlineModeEdit } from 'react-icons/md'
import { format } from 'date-fns'
import { getAssetUrl } from 'lib'
import { useAppContext } from 'hooks/useAppContext'
import { createAdhocQuoteNote, deleteAdhocQuoteNote, getAdhocQuoteNotes, updateAdhocQuoteNote } from 'api'

export const ModalNotes = ({ isOpen, onClose, quote, reload }) => {
  const { createToast, currentUser } = useAppContext()
  const [notes, setNotes] = useState([])
  const [selectedNote, setSelectedNote] = useState(null)
  const [formType, setFormType] = useState(NoteFormType.Add)

  const disclosure = useDisclosure()
  const [isRemoving, setIsRemoving] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [clearForm, setClearForm] = useState(0)

  const load = async () => {
    if (!quote?.id) {
      return
    }

    try {
      const { rows } = await getAdhocQuoteNotes(quote?.id)
      setNotes(rows)
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
  }

  useEffect(() => {
    load()
  }, [quote?.id])

  const onEdit = (note) => {
    setFormType(NoteFormType.Edit)
    setSelectedNote(note)
  }

  const onRemove = (note) => {
    setSelectedNote(note)
    disclosure.onOpen()
  }

  const handleAdd = async (content) => {
    setIsSubmitting(true)
    try {
      await createAdhocQuoteNote(quote.id, {
        quoteId: quote.id,
        userId: currentUser.id,
        content
      })
      createToast('success', 'Note added.')
      setIsSubmitting(false)
      setClearForm(clearForm + 1)
      load()
      reload()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleEdit = async (noteId, content) => {
    setIsSubmitting(true)
    try {
      await updateAdhocQuoteNote(quote.id, noteId, {
        quoteId: quote.id,
        userId: currentUser.id,
        content
      })
      createToast('success', 'Note updated.')
      setIsSubmitting(false)
      setClearForm(clearForm + 1)
      setFormType(NoteFormType.Add)
      setSelectedNote(null)
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setFormType(NoteFormType.Add)
    setSelectedNote(null)
  }

  const handleRemove = async () => {
    setIsRemoving(true)
    try {
      await deleteAdhocQuoteNote(quote.id, selectedNote.id)
      createToast('success', 'Note removed.')
      disclosure.onClose()
      setSelectedNote(null)
      setIsRemoving(false)
      load()
      reload()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  return (
    <>
      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Notes for {quote?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack gap={4}>
              {!notes.length && (
                <Text fontSize="sm">Notes note added.</Text>
              )}
              {notes.map(note => (
                <NoteRow
                  key={note.id}
                  note={note}
                  onEdit={() => onEdit(note)}
                  onRemove={() => onRemove(note)}
                />
              ))}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <NoteForm
              type={formType}
              isSubmitting={isSubmitting}
              onAdd={handleAdd}
              onSave={(v) => handleEdit(selectedNote?.id, v)}
              onCancel={() => handleCancel(selectedNote?.id)}
              clearForm={clearForm}
              note={selectedNote}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ModalRemoveNote
        isRemoving={isRemoving}
        handleRemove={handleRemove}
        {...disclosure}
      />
    </>
  )
}

ModalNotes.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  quote: PropTypes.object,
  reload: PropTypes.func
}

const ModalRemoveNote = ({ isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Note</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the note?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveNote.propTypes = {
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}

const NoteRow = ({ note, onEdit, onRemove }) => {
  const { currentUser } = useAppContext()

  return (
    <VStack
      gap={2}
      w="100%"
      alignItems="flex-start"
    >
      <HStack
        w="100%"
        position="relative"
      >
        <Image w="40px" h="40px" src={getAssetUrl(note.image)} borderRadius="full" />
        <Box>
          <Text fontSize="sm" fontWeight="bold">{note.firstname} {note.lastname}</Text>
          <Text fontSize="xs">{format(new Date(note.createdAt), 'MM/dd/yyyy hh:mm aaa')}</Text>
        </Box>
        {currentUser?.id === note.userId && (
          <Flex
            position="absolute"
            right={0}
            columnGap={2}
          >
            <Button variant="ghost" size="sm" onClick={onEdit}><MdOutlineModeEdit size="16px" /></Button>
            <Button variant="ghost" size="sm" onClick={onRemove}><MdDeleteOutline size="16px" /></Button>
          </Flex>
        )}
      </HStack>
      <Text fontSize="sm" pl="48px">{note.content}</Text>
    </VStack>
  )
}

NoteRow.propTypes = {
  note: PropTypes.object,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func
}

const NoteFormType = {
  Add: 'add',
  Edit: 'edit'
}

const NoteForm = ({ type, onAdd, onSave, onCancel, clearForm, note = null }) => {
  const [content, setContent] = useState('')

  useEffect(() => {
    if (type !== NoteFormType.Edit) {
      return
    }

    setContent(note?.content || '')
  }, [type, note?.content])

  useEffect(() => {
    setContent('')
  }, [clearForm])

  const handleCancel = () => {
    setContent('')
    onCancel()
  }

  return (
    <Box w="100%">
      <Textarea mb={2} rows={2} value={content} onChange={e => setContent(e.target.value)} />
      <Flex justifyContent="flex-end" columnGap={2}>
        {type === NoteFormType.Add && (
          <Button size="sm" colorScheme="blue" onClick={() => onAdd(content)}>Add</Button>
        )}
        {type === NoteFormType.Edit && (
          <>
            <Button size="sm" colorScheme="blue" onClick={() => onSave(content)}>Save</Button>
            <Button size="sm" colorScheme="gray" onClick={handleCancel}>Cancel</Button>
          </>
        )}
      </Flex>
    </Box>
  )
}

NoteForm.propTypes = {
  type: PropTypes.oneOf(Object.values(NoteFormType)).isRequired,
  onAdd: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  clearForm: PropTypes.any.isRequired,
  note: PropTypes.object
}
